import { type ComponentProps, memo } from "react";
import { Link } from "react-router-dom";
import { type Theme, useTheme } from "react-better-html";
import ReactMarkdown, { type Components, type ExtraProps } from "react-markdown";
import styled from "styled-components";
import remarkGfm from "remark-gfm";

const remarkPlugins: React.ComponentProps<typeof ReactMarkdown>["remarkPlugins"] = [remarkGfm];

const WrapperElement = styled.div.withConfig({
   shouldForwardProp: (prop) => !["theme"].includes(prop),
})<{ theme: Theme }>`
   h1,
   h2 {
      margin-top: ${(props) => props.theme.styles.space * 3}px;
      margin-bottom: ${(props) => props.theme.styles.space}px;
   }

   h3,
   h4,
   h5,
   h6 {
      margin-top: ${(props) => props.theme.styles.space + props.theme.styles.gap}px;
      margin-bottom: ${(props) => props.theme.styles.gap}px;
   }

   h1:first-child {
      margin-top: 0px;
   }

   h2 {
      border-bottom: 1px solid ${(props) => props.theme.colors.border};
      padding-bottom: ${(props) => props.theme.styles.gap}px;
   }

   p {
      margin-bottom: ${(props) => props.theme.styles.gap}px;
   }

   code {
      font-family: inherit;
      color: ${(props) => props.theme.colors.primary};
      background-color: ${(props) => props.theme.colors.primary + "30"};
      border-radius: ${(props) => props.theme.styles.borderRadius / 2}px;
      padding-inline: 4px;
   }

   ul,
   ol {
      margin-bottom: ${(props) => props.theme.styles.space}px;

      p {
         margin-bottom: 0px;
      }

      li {
         margin-left: ${(props) => props.theme.styles.space + props.theme.styles.gap}px;
         margin-bottom: ${(props) => props.theme.styles.gap}px;

         &::marker {
            color: ${(props) => props.theme.colors.primary};
         }

         ul {
            margin-top: ${(props) => props.theme.styles.gap / 2}px;
         }
      }
   }

   img {
      display: block;
      width: 100%;
      max-width: 320px;
      border-radius: ${(props) => props.theme.styles.borderRadius}px;

      &.large {
         max-width: 560px;
      }

      &.small {
         max-width: 200px;
      }
   }

   a {
      color: ${(props) => props.theme.colors.primary};
      text-decoration: none;

      &:hover {
         text-decoration: underline;
      }
   }

   hr {
      border: none;
      border-top: 1px solid ${(props) => props.theme.colors.border};
      margin-block: ${(props) => props.theme.styles.space}px;
   }

   .tableWrapper {
      overflow-x: auto;
      margin-bottom: ${(props) => props.theme.styles.space}px;

      table {
         width: 100%;
         border-collapse: collapse;

         tr {
            th {
               background-color: ${(props) => props.theme.colors.border};
               padding: ${(props) => props.theme.styles.gap}px ${(props) => props.theme.styles.gap}px;
            }

            td {
               padding: ${(props) => props.theme.styles.gap / 2}px ${(props) => props.theme.styles.gap}px;
            }
         }
      }
   }

   @media screen and (max-width: 800px) {
      img {
         margin-inline: auto;
      }
   }
`;

type MarkdownAProps = ComponentProps<"a"> & ExtraProps;

const MarkdownA = memo(function MarkdownA({ node, href, children, ...aProps }: MarkdownAProps) {
   return (
      <Link to={href ?? ""} {...aProps}>
         {children}
      </Link>
   );
});

type MarkdownTableProps = ComponentProps<"table"> & ExtraProps;

const MarkdownTable = memo(function MarkdownTable({ node, children, ...tableProps }: MarkdownTableProps) {
   return (
      <div className="tableWrapper">
         <table {...tableProps}>{children}</table>
      </div>
   );
});

const markdownComponents: Components = {
   a: MarkdownA,
   table: MarkdownTable,
};

type MarkdownProps = {
   children?: string;
};

function Markdown({ children }: MarkdownProps) {
   const theme = useTheme();

   return (
      <WrapperElement theme={theme}>
         <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
            {children}
         </ReactMarkdown>
      </WrapperElement>
   );
}

export default memo(Markdown);
